/**
 * Everything the user can do, applied here first and sent afterwards.
 *
 * Each action writes to the local copy, queues the mutation, and returns. Nothing waits on the
 * network. A tap in a basement freezer behaves exactly like a tap on wifi, which is the only
 * way the inventory stays true — an app that stalls when the signal drops is an app people
 * stop updating, and a stale inventory is worse than none.
 */

import { ulid } from 'ulid';

import type {
  Food,
  FoodLookup,
  Grocery,
  InventoryItem,
  ItemSource,
  Level,
  Location,
  LocationKind,
  NewGrocery,
  ShoppingEntry,
} from './api/types';
import { resolveFood } from './domain/catalog';
import { expiryOf, inferExpiry } from './domain/expiry';
import {
  draftGrocery,
  groceryFromLookup,
  isStaple,
  resolveGrocery,
  stockToFollow,
} from './domain/groceries';
import { needsRestocking, refills } from './domain/levels';
import { restockable } from './domain/restocking';
import { isOutstanding } from './domain/shopping';
import type { Database } from './store/database';
import {
  forgetGrocery,
  groceries,
  items,
  reviseItem,
  saveGrocery,
  saveItem,
  saveShoppingEntry,
  shoppingList,
  type Snapshot,
} from './store/entities';
import { enqueue } from './store/outbox';

/** Something the user just did, and how to take it back. */
export interface Undoable {
  /** Phrased for a person: "Milk set to low". */
  description: string;
  undo(): Promise<void>;
}

/**
 * What the actions need from outside the local database.
 *
 * A struct rather than a run of positional arguments, because there are now more than three
 * and the fourth is a function whose position nobody would remember.
 */
export interface Surroundings {
  /** The seeded catalog, as last fetched. Empty is survivable; the lookup covers it. */
  catalog: () => Food[];
  locations: () => Location[];
  /**
   * Asks the API about a food the catalog does not know.
   *
   * Injected rather than reached for through the client, so every rule below this line stays
   * testable without a network — and so the one call in the app that costs money has exactly
   * one place it can be made from.
   */
  lookUp: (name: string) => Promise<FoodLookup>;
}

export class Actions {
  /**
   * Lookups already in flight, by slug.
   *
   * The one call in this app that costs money is also the one slow enough to be tapped twice,
   * and the read-back below only catches a second attempt made *after* the first has finished.
   * Two at once would be two model calls, two entries under one slug, and a queued write the
   * server refuses. Sharing the promise means the second caller waits on the first answer.
   *
   * Not only a guard against impatient thumbs: checking several things off the shopping list
   * in a row is a described use of this app, and nothing makes those wait for each other.
   */
  private readonly beingListed = new Map<string, Promise<Grocery | null>>();

  constructor(
    private readonly db: Database,
    private readonly surroundings: Surroundings,
  ) {}

  /**
   * Adds something the household now has.
   *
   * It takes a grocery rather than a name, and that is the whole shape of the redesign: where
   * it goes and how long it keeps are already answered, on the entry, by somebody who had time
   * to think about it. Adding is left with nothing to ask.
   *
   * The list half is not a nicety. An entry the app added because this food ran low has served
   * its purpose the moment the food is back, and a list still asking for milk beside a fridge
   * holding milk is the state that stops the whole loop being believed.
   */
  async addItem(grocery: Grocery, level: Level = 'plenty'): Promise<InventoryItem> {
    const item = this.stocked(grocery, { level, source: 'manual' });

    await saveItem(this.db, item);
    await enqueue(this.db, {
      op: 'add_item',
      item: {
        id: item.id,
        name: grocery.name,
        location_id: grocery.location_id,
        ...(level === 'plenty' ? {} : { level }),
      },
    });
    await this.retractAutomaticEntriesFor(grocery.name);

    return item;
  }

  /**
   * Puts a food the household has not listed yet onto the list, guessing every answer.
   *
   * Nothing is asked. The seeded catalog answers for the ninety-six foods it covers; anything
   * else — a store brand, a soft drink, a misspelling — goes to the API, which asks a model
   * once and hands back a corrected name, a place, and a shelf life. Either way the answer is
   * written into the household's own list, so the same food is never looked up twice.
   *
   * When nobody can place it the entry is still created, with the name as typed and no dates,
   * marked as needing a look. That is deliberate: the item the user is adding lands either
   * way, which is the promise of a local-first client, and the row says plainly that the app
   * is guessing rather than inventing a date somebody would come to trust.
   */
  async listGrocery(name: string): Promise<Grocery | null> {
    const draft = draftGrocery(
      name,
      this.surroundings.catalog(),
      this.surroundings.locations(),
    );
    if (draft === null) {
      return null;
    }

    const slug = draft.slug ?? name;
    const underway = this.beingListed.get(slug);
    if (underway !== undefined) {
      return underway;
    }

    const listing = this.list(draft, slug).finally(() => this.beingListed.delete(slug));
    this.beingListed.set(slug, listing);

    return listing;
  }

  /** Everything listing a food actually does, once it is settled that nobody else is doing it. */
  private async list(draft: NewGrocery, slug: string): Promise<Grocery> {
    // Read back rather than taken from what is on screen. A shopping run checks off several
    // things in a row without the screen reloading between them, and a stale copy would mint
    // the same entry twice — two entries under one slug, the second of which the server
    // refuses. It is also what makes "looked up once" true rather than nearly true.
    const held = (await groceries(this.db)).find((listed) => listed.slug === slug);
    if (held !== undefined) {
      return held;
    }

    const grocery =
      draft.source === 'catalog'
        ? ({
            slug,
            name: draft.name,
            location_id: draft.location_id,
            shelf_life_days: draft.shelf_life_days ?? null,
            opened_shelf_life_days: draft.opened_shelf_life_days ?? null,
            source: 'catalog',
          } as Grocery)
        : await this.askAbout(draft.name, slug);

    await saveGrocery(this.db, grocery);
    await enqueue(this.db, {
      op: 'add_grocery',
      grocery: {
        slug: grocery.slug,
        name: grocery.name,
        location_id: grocery.location_id,
        source: grocery.source,
        ...(grocery.shelf_life_days === null
          ? {}
          : { shelf_life_days: grocery.shelf_life_days }),
        ...(grocery.opened_shelf_life_days === null
          ? {}
          : { opened_shelf_life_days: grocery.opened_shelf_life_days }),
      },
    });

    return grocery;
  }

  /**
   * What the API can tell us about a food the catalog has never heard of.
   *
   * A failure here is not an error the user should see. Offline in a basement pantry is the
   * ordinary case, and the answer is the same one the API gives for a name it cannot place:
   * record it, say so on the row, and let the item land.
   */
  private async askAbout(asked: string, slug: string): Promise<Grocery> {
    const unplaceable: Grocery = {
      slug,
      name: asked.trim(),
      location_id: this.surroundings.locations()[0]?.id ?? '',
      shelf_life_days: null,
      opened_shelf_life_days: null,
      source: 'unknown',
    };

    try {
      const found = await this.surroundings.lookUp(asked);

      return groceryFromLookup(found, this.surroundings.locations(), slug) ?? unplaceable;
    } catch {
      return unplaceable;
    }
  }

  /**
   * Records an entry as the user has just edited it.
   *
   * Saving is itself the household's answer, so the entry stops being the catalog's or the
   * model's and becomes theirs — which is what takes the "needs a look" off a row nobody could
   * place. The server stamps the same thing; this copy is so the badge goes at the moment of
   * the tap rather than at the next sync.
   *
   * Where the entry has been pointed somewhere new, the stock it placed follows it — see
   * `stockToFollow`. That is the one edit here that changes what is already in the kitchen, so
   * it is the one that hands back a way to reverse it.
   */
  async saveGrocery(grocery: Grocery): Promise<Undoable | null> {
    const previous = (await groceries(this.db)).find(
      (candidate) => candidate.slug === grocery.slug,
    );

    await saveGrocery(this.db, { ...grocery, source: 'user' });
    await enqueue(this.db, {
      op: 'save_grocery',
      slug: grocery.slug,
      grocery: {
        name: grocery.name,
        location_id: grocery.location_id,
        ...(grocery.shelf_life_days === null
          ? {}
          : { shelf_life_days: grocery.shelf_life_days }),
        ...(grocery.opened_shelf_life_days === null
          ? {}
          : { opened_shelf_life_days: grocery.opened_shelf_life_days }),
      },
    });

    if (previous === undefined || previous.location_id === grocery.location_id) {
      return null;
    }

    return this.movedWithEntry(previous, grocery);
  }

  /**
   * Takes an entry's stock to where the entry now points, and offers the whole thing back.
   *
   * Undo returns the entry as well as the items: correcting a location and moving what it
   * placed are one act to the person who did it, so they reverse in one gesture rather than
   * leaving half of it behind.
   */
  private async movedWithEntry(previous: Grocery, grocery: Grocery): Promise<Undoable | null> {
    const following = stockToFollow(await items(this.db), grocery.slug, previous.location_id);
    for (const item of following) {
      await this.setLocation(item.id, grocery.location_id);
    }

    if (following.length === 0) {
      return null;
    }

    const place = this.surroundings.locations().find((l) => l.id === grocery.location_id);

    return {
      description: `${grocery.name} — moved to ${place?.name ?? 'somewhere'}, ${stockCount(following.length)}`,
      undo: async () => {
        await saveGrocery(this.db, previous);
        await enqueue(this.db, {
          op: 'save_grocery',
          slug: previous.slug,
          grocery: {
            name: previous.name,
            location_id: previous.location_id,
            ...(previous.shelf_life_days === null
              ? {}
              : { shelf_life_days: previous.shelf_life_days }),
            ...(previous.opened_shelf_life_days === null
              ? {}
              : { opened_shelf_life_days: previous.opened_shelf_life_days }),
          },
        });
        for (const item of following) {
          await this.setLocation(item.id, previous.location_id);
        }
      },
    };
  }

  private async setLocation(itemId: string, locationId: string): Promise<void> {
    await reviseItem(this.db, itemId, { location_id: locationId });
    await enqueue(this.db, {
      op: 'patch_item',
      id: itemId,
      patch: { location_id: locationId },
    });
  }

  /**
   * Stops buying something.
   *
   * What is already in the inventory stays, holding the dates this entry gave it. Undo puts
   * the entry back rather than asking first, for the same reason removing an item does: a
   * confirmation taxes every correct gesture to catch the rare wrong one.
   */
  async removeGrocery(grocery: Grocery): Promise<Undoable> {
    await forgetGrocery(this.db, grocery.slug);
    await enqueue(this.db, { op: 'remove_grocery', slug: grocery.slug });

    return {
      description: `${grocery.name} — off the list`,
      undo: async () => {
        await saveGrocery(this.db, grocery);
        await enqueue(this.db, {
          op: 'add_grocery',
          grocery: {
            slug: grocery.slug,
            name: grocery.name,
            location_id: grocery.location_id,
            source: grocery.source,
            ...(grocery.shelf_life_days === null
              ? {}
              : { shelf_life_days: grocery.shelf_life_days }),
            ...(grocery.opened_shelf_life_days === null
              ? {}
              : { opened_shelf_life_days: grocery.opened_shelf_life_days }),
          },
        });
      },
    };
  }

  /**
   * Walks an item's level round by one, which is the app's most-used gesture.
   *
   * Dropping to `low` or `out` also puts the item on the shopping list, closing the loop the
   * product is built around: the moment you notice you are running out is the only moment you
   * will reliably record it.
   *
   * Running out no longer retires the item. The line stays, empty, because the household's
   * statement that they buy this is the most useful thing on the row — and the tap that
   * emptied it is the same one that fills it again.
   */
  async cycleLevel(item: InventoryItem, to: Level): Promise<Undoable> {
    if (refills(item, to)) {
      return this.refill(item, to);
    }

    if (needsRestocking(to) && !needsRestocking(item.level)) {
      await this.addToShoppingList(item.name, item.location_id, 'auto_low');
    }

    await this.setLevel(item.id, to);

    return {
      description: `${item.name} — ${to}`,
      undo: () => this.setLevel(item.id, item.level),
    };
  }

  /**
   * Putting stock back on a line that had run out.
   *
   * A restock rather than a level change, and it has to be. The line has been sitting empty
   * for however long, so the date it carries describes food that is gone: coming back as
   * `plenty` with a three-week-old date would put fresh milk straight into "Use now", which is
   * the wrong date the app would then insist on. Restocking recomputes it from today and takes
   * the food off the shopping list, which is the whole reason it went on.
   *
   * Reversing it is the same gesture in the other direction — empty the line again, which puts
   * the food back on the list. That is why this one restock is offered back where the one in
   * quick-add is not: what an undo cannot restore is the old dates, and a line with nothing on
   * it has no dates worth restoring.
   */
  private async refill(item: InventoryItem, to: Level): Promise<Undoable> {
    const refilled = await this.restock(item, to);

    return {
      description: `${refilled.name} — restocked`,
      undo: async () => {
        await this.cycleLevel(refilled, 'out');
      },
    };
  }

  /**
   * Takes an item out of the inventory, reversibly.
   *
   * Nothing is deleted — `gone` is a state — so taking it back is a state change rather than a
   * resurrection. That is what lets a swipe remove something with no confirmation: a
   * confirmation taxes every correct gesture to catch the rare wrong one, where undo only ever
   * costs the mistake.
   *
   * How the item went is not asked. The API still records eaten and thrown out separately and
   * still defaults to eaten, but the only thing that reads the difference is shelf-life
   * learning, which nothing calls — so asking would be charging the user for a feature that is
   * not running. See the known gaps in AGENTS.md.
   */
  async removeItem(item: InventoryItem): Promise<Undoable> {
    await reviseItem(this.db, item.id, { state: 'gone' });
    await enqueue(this.db, { op: 'remove_item', id: item.id });

    return {
      description: `${item.name} — removed`,
      undo: () => this.restoreItem(item),
    };
  }

  /**
   * Puts back an item that was removed by mistake.
   *
   * The removal is queued alongside this rather than cancelled out. Both have already been
   * given keys, and dropping the first would leave the second describing a removal the server
   * never saw.
   */
  private async restoreItem(item: InventoryItem): Promise<void> {
    await reviseItem(this.db, item.id, { state: 'active' });
    await enqueue(this.db, { op: 'restore_item', id: item.id });
  }

  /**
   * Replenishes something the household already has, rather than adding a second row for it.
   *
   * The new stock starts its own shelf life. A fresh carton is not six days old because the
   * last one was, and it is not opened because the last one had been.
   */
  async restock(item: InventoryItem, level: Level = 'plenty'): Promise<InventoryItem> {
    const restocked = await this.replenished(item, level);
    await enqueue(this.db, { op: 'restock_item', id: item.id, restock: { level } });
    await this.retractAutomaticEntriesFor(item.name);

    return restocked;
  }

  /** The local half of a restock, shared with checking the same food off the shopping list. */
  private async replenished(item: InventoryItem, level: Level): Promise<InventoryItem> {
    const now = new Date();
    const listed = await this.listing(item.name, item.food_ref);
    const restocked: InventoryItem = {
      ...item,
      level,
      state: 'active',
      added_at: now.toISOString(),
      opened_at: null,
      expires_at: this.freshDate(item, listed, now),
      updated_at: now.toISOString(),
    };

    await saveItem(this.db, restocked);

    return restocked;
  }

  /**
   * Takes off the list anything the app itself put there for this food.
   *
   * Automation may reverse automation; only the user may reverse the user. An entry added
   * because a carton ran low has served its purpose the moment that food is back, and leaving
   * it means the list still says "milk" while the fridge has four. An entry the household
   * typed stays, because it was typed for a reason nothing here can see.
   */
  private async retractAutomaticEntriesFor(name: string): Promise<void> {
    const stale = (await shoppingList(this.db)).filter(
      (entry) =>
        entry.source === 'auto_low' && isOutstanding(entry) && sameName(entry.name, name),
    );

    for (const entry of stale) {
      await saveShoppingEntry(this.db, {
        ...entry,
        retracted_at: new Date().toISOString(),
      });
      await enqueue(this.db, { op: 'retract_shopping', id: entry.id });
    }
  }

  /**
   * What an arrival of this food would replenish, if anything.
   *
   * Asked before adding rather than decided afterwards, because the answer changes what the
   * user is offered — and standing in the kitchen holding the carton is the one moment when
   * they can see which it is.
   */
  async alreadyHere(grocery: Grocery): Promise<InventoryItem | null> {
    return restockable(await items(this.db), {
      foodRef: grocery.slug,
      locationId: grocery.location_id,
      kind: this.kindOf(grocery.location_id),
      isStaple: isStaple(grocery),
    });
  }

  /** Gives an item more time, which is how "no, it is still fine" is answered. */
  async keepLonger(item: InventoryItem, days: number): Promise<Undoable> {
    const extended = new Date(Date.now() + days * 86_400_000).toISOString();
    const previous = item.expires_at;

    await this.setExpiry(item.id, extended);

    return {
      description: `${item.name} — ${days} more days`,
      undo: async () => {
        if (previous !== null) {
          await this.setExpiry(item.id, previous);
        }
      },
    };
  }

  async addToShoppingList(
    name: string,
    targetLocationId: string | null,
    source: 'auto_low' | 'manual' = 'manual',
  ): Promise<void> {
    // Running out of the same thing twice in a week should not put it on the list twice.
    const alreadyListed = await shoppingList(this.db);
    if (alreadyListed.some((entry) => isOutstanding(entry) && sameName(entry.name, name))) {
      return;
    }

    const listed = await this.listing(name, null);
    const id = ulid();
    const entry: ShoppingEntry = {
      id,
      name,
      food_ref: listed?.slug ?? null,
      source,
      target_location_id: targetLocationId ?? listed?.location_id ?? null,
      added_at: new Date().toISOString(),
      purchased_at: null,
      retracted_at: null,
    };

    await saveShoppingEntry(this.db, entry);
    await enqueue(this.db, {
      op: 'add_shopping',
      entry: {
        id,
        name,
        source,
        ...(entry.target_location_id === null
          ? {}
          : { target_location_id: entry.target_location_id }),
      },
    });
  }

  /**
   * Checking an entry off in the store.
   *
   * The entry closes and the item appears at once, with its place and date already worked out
   * — the point being that thirty items after a grocery run become thirty taps on a list the
   * user was already holding. A food nobody has listed is listed here, silently, rather than
   * stopping to ask at a till.
   */
  async purchase(entry: ShoppingEntry): Promise<void> {
    const grocery =
      (await this.listing(entry.name, entry.food_ref)) ?? (await this.listGrocery(entry.name));
    if (grocery === null) {
      return;
    }

    const here = await this.alreadyHere(grocery);

    await saveShoppingEntry(this.db, { ...entry, purchased_at: new Date().toISOString() });

    // Buying more of something the household still has replenishes that line. Creating a
    // second row is how the list that exists to close the loop opens a new one every week.
    if (here !== null) {
      await this.replenished(here, 'plenty');
      await enqueue(this.db, {
        op: 'purchase_shopping',
        id: entry.id,
        purchase: { location_id: grocery.location_id, restock_item_id: here.id },
      });

      return;
    }

    const item = this.stocked(grocery, { level: 'plenty', source: 'shopping_list' });
    await saveItem(this.db, item);
    await enqueue(this.db, {
      op: 'purchase_shopping',
      id: entry.id,
      purchase: { location_id: grocery.location_id, item_id: item.id },
    });
  }

  /**
   * The record for something arriving, built entirely from the household's own entry.
   *
   * The expiry is worked out locally so the row shows a date the instant it appears. The
   * server recomputes it from the same entry; if the two ever disagree, the server's answer
   * wins at the next refresh.
   */
  private stocked(
    grocery: Grocery,
    arrival: { level: Level; source: ItemSource },
  ): InventoryItem {
    const now = new Date();
    const kind = this.kindOf(grocery.location_id);

    return {
      id: ulid(),
      location_id: grocery.location_id,
      name: grocery.name,
      food_ref: grocery.slug,
      level: arrival.level,
      state: 'active',
      is_staple: isStaple(grocery),
      added_at: now.toISOString(),
      opened_at: null,
      frozen_at: kind === 'freezer' ? now.toISOString() : null,
      expires_at: expiryOf(grocery, now, null)?.toISOString() ?? null,
      expiry_source: 'inferred',
      source: arrival.source,
      note: null,
      updated_at: now.toISOString(),
      updated_by: 'you',
    };
  }

  /**
   * The date a replenished line should now carry.
   *
   * The household's own entry answers it. The seeded catalog is the fallback, and it is not
   * dead code: items added before this household had a list still point at a catalog slug and
   * nothing has re-listed them. It is also what the server falls back to, so the two agree.
   */
  private freshDate(item: InventoryItem, listed: Grocery | null, now: Date): string | null {
    if (listed !== null) {
      return expiryOf(listed, now, null)?.toISOString() ?? null;
    }

    const food =
      item.food_ref === null ? null : resolveFood(this.surroundings.catalog(), item.food_ref);
    if (food === null) {
      return item.expires_at;
    }

    return (
      inferExpiry({
        food,
        location: this.kindOf(item.location_id),
        addedAt: now,
        openedAt: null,
      })?.toISOString() ?? null
    );
  }

  private async setLevel(itemId: string, level: Level): Promise<void> {
    await reviseItem(this.db, itemId, { level });
    await enqueue(this.db, { op: 'patch_item', id: itemId, patch: { level } });
  }

  private async setExpiry(itemId: string, expiresAt: string): Promise<void> {
    await reviseItem(this.db, itemId, {
      expires_at: expiresAt,
      expiry_source: 'explicit',
      state: 'active',
    });
    await enqueue(this.db, {
      op: 'patch_item',
      id: itemId,
      patch: { expires_at: expiresAt },
    });
  }

  /**
   * The household's entry for a food, found by its slug first and its name second.
   *
   * The slug is what an item carries and what survives a rename, so it is the better key.
   * The name is the fallback for records written before the food was ever listed.
   */
  private async listing(name: string, foodRef: string | null): Promise<Grocery | null> {
    const held = await groceries(this.db);
    const bySlug = foodRef === null ? undefined : held.find((one) => one.slug === foodRef);

    return bySlug ?? resolveGrocery(held, name);
  }

  /** An unrecognised location is treated as dry storage, the same as the server does. */
  private kindOf(locationId: string): LocationKind {
    return (
      this.surroundings.locations().find((location) => location.id === locationId)?.kind ??
      'pantry'
    );
  }
}

export type { Snapshot };

function sameName(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** "one item" reads as a sentence where "1 items" reads as a bug. */
function stockCount(count: number): string {
  return count === 1 ? 'and the one in it' : `and the ${count} in it`;
}
